<template>
  <div class="min-h-screen bg-gray-50 py-12">
    <!-- Schema Markup -->
    <SchemaMarkup 
      type="Article"
      :data="articleSchema"
    />
    <SchemaMarkup 
      type="BreadcrumbList"
      :data="breadcrumbSchema"
    />
    
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Breadcrumb -->
      <nav class="mb-8">
        <ol class="flex flex-wrap items-center space-x-2 text-sm text-gray-500">
          <li><router-link to="/" class="hover:text-blue-600">Trang chủ</router-link></li>
          <li>/</li>
          <li><router-link to="/blog" class="hover:text-blue-600">Blog</router-link></li>
          <li>/</li>
          <li class="text-gray-900">Core Web Vitals 2025 Advanced</li>
        </ol>
      </nav>

      <!-- Article Header -->
      <header class="mb-8">
        <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Core Web Vitals 2025: Hướng dẫn tối ưu LCP, CLS, INP từ A-Z</h1>
        <div class="flex items-center text-sm text-gray-500 mb-6">
          <span>06/01/2025</span>
          <span class="mx-2">•</span>
          <span>12 phút đọc</span>
          <span class="mx-2">•</span>
          <span>SEO & Performance</span>
        </div>
        <div class="h-48 sm:h-56 lg:h-64 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
          <svg class="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
      </header>

      <!-- Article Content -->
      <article class="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
        <div class="prose prose-lg max-w-none">
          <h2>Tại sao Core Web Vitals quan trọng trong 2025?</h2>
          <p>Core Web Vitals là bộ chỉ số quan trọng nhất của Google để đánh giá trải nghiệm người dùng trên website. Năm 2025, Google vẫn tập trung vào 3 chỉ số chính nhưng với các tiêu chuẩn ngày càng khắt khe hơn.</p>
          
          <div class="bg-blue-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="text-xl font-semibold mb-4">3 Chỉ số Core Web Vitals 2025</h3>
            <ul class="space-y-3">
              <li><strong>LCP (Largest Contentful Paint)</strong> – Thời gian tải nội dung lớn nhất (mục tiêu ≤ 2.5s)</li>
              <li><strong>CLS (Cumulative Layout Shift)</strong> – Độ ổn định layout (mục tiêu ≤ 0.1)</li>
              <li><strong>INP (Interaction to Next Paint)</strong> – Thời gian phản hồi tương tác (mục tiêu ≤ 200ms)</li>
            </ul>
          </div>

          <h2>1. Tối ưu LCP (Largest Contentful Paint) - Phiên bản 2025</h2>
          <p>LCP đo thời gian tải nội dung lớn nhất trên trang. Đây thường là hình ảnh hero, video, hoặc khối text lớn.</p>
          
          <h3>Các bước tối ưu LCP nâng cao:</h3>
          <ol>
            <li><strong>Preload critical resources với priority hints:</strong>
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>&lt;link rel="preload" href="hero-image.webp" as="image" fetchpriority="high"&gt;
&lt;link rel="preload" href="critical.css" as="style"&gt;
&lt;link rel="preload" href="font.woff2" as="font" type="font/woff2" crossorigin&gt;</code></pre>
            </li>
            <li><strong>Optimize images với AVIF và WebP:</strong>
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>&lt;picture&gt;
  &lt;source srcset="image.avif" type="image/avif"&gt;
  &lt;source srcset="image.webp" type="image/webp"&gt;
  &lt;img src="image.jpg" loading="eager" alt="description"&gt;
&lt;/picture&gt;</code></pre>
            </li>
            <li><strong>Critical CSS inlining và resource hints:</strong>
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>&lt;style&gt;
  /* Critical CSS inline */
&lt;/style&gt;
&lt;link rel="preload" href="non-critical.css" as="style" onload="this.onload=null;this.rel='stylesheet'"&gt;</code></pre>
            </li>
            <li><strong>HTTP/3 và Early Hints:</strong> Sử dụng HTTP/3 và Early Hints để tăng tốc độ tải</li>
          </ol>

          <h2>2. Tối ưu CLS (Cumulative Layout Shift) - Cập nhật 2025</h2>
          <p>CLS đo độ ổn định của layout. Layout shift xảy ra khi các element thay đổi vị trí đột ngột.</p>
          
          <h3>Nguyên nhân gây CLS và giải pháp:</h3>
          <ul>
            <li><strong>Hình ảnh không có kích thước:</strong>
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>&lt;img src="image.jpg" width="800" height="600" alt="description" style="aspect-ratio: 800/600"&gt;</code></pre>
            </li>
            <li><strong>Fonts loading với font-display:</strong>
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>@font-face {
  font-family: 'Custom Font';
  src: url('font.woff2') format('woff2');
  font-display: swap;
}</code></pre>
            </li>
            <li><strong>Dynamic content với skeleton loading:</strong>
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>&lt;div class="skeleton" style="height: 200px; background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);"&gt;&lt;/div&gt;</code></pre>
            </li>
          </ul>

          <h2>3. Tối ưu INP (Interaction to Next Paint) - Mới trong 2025</h2>
          <p>INP thay thế FID từ 2024, đo thời gian từ khi user tương tác đến khi browser có thể render frame tiếp theo.</p>
          
          <h3>Giải pháp tối ưu INP:</h3>
          <ol>
            <li><strong>Code splitting và lazy loading:</strong>
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>// Vue.js example
const LazyComponent = defineAsyncComponent(() => import('./LazyComponent.vue'))

// JavaScript example
const loadModule = () => import('./heavy-module.js')</code></pre>
            </li>
            <li><strong>Web Workers cho heavy tasks:</strong>
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>// main.js
const worker = new Worker('heavy-task.js')
worker.postMessage(data)

// heavy-task.js
self.onmessage = function(e) {
  const result = heavyComputation(e.data)
  self.postMessage(result)
}</code></pre>
            </li>
            <li><strong>RequestIdleCallback cho non-critical tasks:</strong>
              <pre class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto text-xs sm:text-sm"><code>function scheduleWork() {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(doWork)
  } else {
    setTimeout(doWork, 1)
  }
}</code></pre>
            </li>
          </ol>

          <h2>4. Tools và Monitoring cho Core Web Vitals 2025</h2>
          <div class="grid md:grid-cols-2 gap-6 my-8">
            <div class="bg-green-50 p-4 rounded-lg">
              <h3 class="font-semibold text-green-800 mb-2">🛠️ Tools Miễn Phí</h3>
              <ul class="text-green-700 space-y-1">
                <li>• Google PageSpeed Insights</li>
                <li>• Chrome DevTools Performance</li>
                <li>• Web Vitals Extension</li>
                <li>• Lighthouse CI</li>
              </ul>
            </div>
            <div class="bg-blue-50 p-4 rounded-lg">
              <h3 class="font-semibold text-blue-800 mb-2">📊 Monitoring Tools</h3>
              <ul class="text-blue-700 space-y-1">
                <li>• Google Search Console</li>
                <li>• Google Analytics 4</li>
                <li>• Real User Monitoring (RUM)</li>
                <li>• Core Web Vitals API</li>
              </ul>
            </div>
          </div>

          <h2>5. Checklist Tối Ưu Core Web Vitals 2025</h2>
          <div class="bg-gray-50 p-6 rounded-lg">
            <h3 class="font-semibold mb-4">✅ LCP Optimization Checklist</h3>
            <ul class="space-y-2">
              <li>□ Preload critical resources với fetchpriority="high"</li>
              <li>□ Optimize images với AVIF/WebP format</li>
              <li>□ Inline critical CSS</li>
              <li>□ Minimize render-blocking resources</li>
              <li>□ Use HTTP/3 và Early Hints</li>
            </ul>
            
            <h3 class="font-semibold mb-4 mt-6">✅ CLS Optimization Checklist</h3>
            <ul class="space-y-2">
              <li>□ Set explicit dimensions cho tất cả media</li>
              <li>□ Use font-display: swap</li>
              <li>□ Reserve space cho dynamic content</li>
              <li>□ Avoid inserting content above existing content</li>
              <li>□ Use skeleton loading cho dynamic content</li>
            </ul>
            
            <h3 class="font-semibold mb-4 mt-6">✅ INP Optimization Checklist</h3>
            <ul class="space-y-2">
              <li>□ Implement code splitting</li>
              <li>□ Use Web Workers cho heavy tasks</li>
              <li>□ Optimize event handlers</li>
              <li>□ Use requestIdleCallback</li>
              <li>□ Minimize main thread blocking</li>
            </ul>
          </div>

          <h2>Kết Luận</h2>
          <p>Tối ưu Core Web Vitals trong 2025 đòi hỏi sự hiểu biết sâu sắc về các chỉ số mới và các công nghệ tối ưu hiện đại. Việc implement đúng các kỹ thuật này không chỉ cải thiện trải nghiệm người dùng mà còn tăng cường SEO ranking và khả năng chuyển đổi của website.</p>
          
          <div class="bg-blue-50 p-4 rounded-lg mt-6">
            <p class="text-blue-800 font-semibold mb-2">💡 Pro Tip:</p>
            <p class="text-blue-700">Sử dụng Google's Core Web Vitals API để monitor real-time performance và nhận alerts khi các chỉ số vượt ngưỡng cho phép.</p>
          </div>
        </div>
      </article>

      <!-- Related Articles -->
      <div class="mt-12 bg-white rounded-lg shadow-lg p-8">
        <h2 class="text-2xl font-bold text-blue-600 mb-6">Bài Viết Liên Quan</h2>
        <div class="grid md:grid-cols-2 gap-6">
          <div class="border rounded-lg p-4 hover:shadow-md transition-shadow">
            <h3 class="font-semibold mb-2">Schema Markup: Hướng dẫn implement từ A-Z</h3>
            <p class="text-sm text-gray-600 mb-3">Cách thêm structured data để tăng CTR và cải thiện SEO</p>
            <span class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">SEO</span>
          </div>
          <div class="border rounded-lg p-4 hover:shadow-md transition-shadow">
            <h3 class="font-semibold mb-2">Page Speed Optimization: 15 kỹ thuật nâng cao</h3>
            <p class="text-sm text-gray-600 mb-3">Tối ưu tốc độ trang web chuyên sâu</p>
            <span class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Performance</span>
          </div>
        </div>
      </div>

      <!-- CTA Section -->
      <div class="mt-12 text-center">
        <router-link to="/contact">
          <button class="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 text-lg font-semibold">
            Cần Tư Vấn Tối Ưu Core Web Vitals?
          </button>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import SchemaMarkup from '../SchemaMarkup.vue'

// Article Schema
const articleSchema = {
  headline: 'Core Web Vitals 2025: Hướng dẫn tối ưu LCP, CLS, INP từ A-Z',
  description: 'Hướng dẫn chi tiết về Core Web Vitals 2025: LCP, CLS, INP với code examples và best practices để tối ưu hiệu suất website.',
  image: 'https://webservices.vn/images/core-web-vitals-2025.webp',
  author: {
    '@type': 'Organization',
    name: 'Web Services',
    url: 'https://webservices.vn'
  },
  publisher: {
    '@type': 'Organization',
    name: 'Web Services',
    logo: {
      '@type': 'ImageObject',
      url: 'https://webservices.vn/favicon.webp'
    }
  },
  datePublished: '2025-01-06T00:00:00+07:00',
  dateModified: '2025-01-06T00:00:00+07:00',
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': 'https://webservices.vn/blog/core-web-vitals-2025'
  },
  articleSection: 'SEO & Performance',
  wordCount: 2500,
  timeRequired: 'PT12M',
  inLanguage: 'vi-VN',
  keywords: ['Core Web Vitals', 'LCP', 'CLS', 'INP', 'SEO', 'Performance', '2025']
}

// Breadcrumb Schema
    const breadcrumbSchema = {
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Trang chủ',
          item: 'https://webservices.vn'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: 'https://webservices.vn/blog'
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Core Web Vitals 2025 Advanced'
        }
      ]
    }
</script>

<style scoped>
.prose h2 {
  @apply text-2xl font-bold text-gray-900 mt-8 mb-4;
}

.prose h3 {
  @apply text-xl font-semibold text-gray-800 mt-6 mb-3;
}

.prose p {
  @apply text-gray-700 leading-relaxed mb-4;
}

.prose ul, .prose ol {
  @apply mb-4;
}

.prose li {
  @apply mb-2;
}
</style>
